import { Box, Paper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCase } from "../../../context/CaseContext";
import { useCurrency } from "../../../context/CurrencyContext";
import { Stage } from "../../../shared/CaseConfiguration";
import { TABULAR_NUMERALS } from "../../general/dataAtoms/shared";
import { NotShown, useRefreshWhilePending } from "../ShownResults";

/**
 * What the server made of a party's value with the settlements the user
 * expects, from the current stage on, beside its value as litigated.
 */
export const AdjustedValue = ({
  party,
  current,
}: {
  party: { id: string; name: string };
  current: Stage;
}) => {
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement.prospects",
  });
  const caseContext = useCase();
  const adjusted = caseContext.getCase().settlementAdjusted;
  const share = useRefreshWhilePending();
  if (!adjusted) return null;
  if (adjusted.status !== "done")
    return (
      <NotShown
        adjusted={adjusted}
        share={share}
        keyPrefix="case.analytics.settlement.prospects"
      />
    );

  const ofParty = adjusted.parties.find(({ partyId }) => partyId === party.id);
  const value = ofParty?.average.stageEvs.find(
    ({ stage }) => stage === current,
  )?.ev;
  const litigated = caseContext
    .getEvMatrix()
    .getOwnEvs(party.id)
    ?.find(({ stage }) => stage === current)?.value;
  if (value === undefined || litigated === undefined) return null;

  const tile = (label: string, amount: number) => (
    <Paper variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 2 }}>
      <Typography
        variant="caption"
        component="p"
        sx={{ color: "text.secondary" }}
      >
        {label}
      </Typography>
      <Typography
        variant="h6"
        component="p"
        sx={{ fontWeight: 600, ...TABULAR_NUMERALS }}
      >
        {currency.format(amount)}
      </Typography>
    </Paper>
  );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 1.5,
      }}
    >
      {tile(t("litigated", { name: party.name }), litigated)}
      {tile(t("gained"), value - litigated)}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: 2,
          bgcolor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        <Typography variant="caption" component="p">
          {t("adjusted")}
        </Typography>
        <Typography
          variant="h5"
          component="p"
          sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}
        >
          {currency.format(value)}
        </Typography>
      </Box>
    </Box>
  );
};
